import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import {
  fetchProjects,
  fetchSettings,
  fetchAllAssets,
} from "../services/apiService";

export const ProjectContext = createContext();

export const ProjectProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [projectList, setProjectList] = useState([]);
  const [assetList, setAssetList] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [currentAsset, setCurrentAsset] = useState(null);
  const [settings, setSettings] = useState(null);

  const currentProjectRef = useRef(currentProject);
  const currentAssetRef = useRef(currentAsset);

  useEffect(() => {
    currentProjectRef.current = currentProject;
    console.log("cp: ", currentProject);
  }, [currentProject]);

  useEffect(() => {
    currentAssetRef.current = currentAsset;
    console.log("ca: ", currentProject);
  }, [currentAsset]);

  const getProjects = useCallback(async () => {
    if (!currentUser) {
      setProjectList([]);
      setCurrentProject(null);
      return;
    }
    try {
      const projects = await fetchProjects();
      const sortedProjects = projects.sort((a, b) =>
        a.code > b.code ? -1 : 1
      );
      setProjectList(sortedProjects);
      // Update current project if it's still in the new list (it may not be,
      // e.g. after a delete — keep the existing selection in that case).
      if (currentProjectRef.current) {
        const updatedProject = sortedProjects.find(
          (project) => project._id === currentProjectRef.current._id
        );
        if (updatedProject) {
          setCurrentProject(updatedProject);
        }
      }
    } catch (error) {
      console.error("Failed to load projects", error);
      setProjectList([]);
      setCurrentProject(null);
    }
  }, [currentUser?._id]);

  const getAssets = useCallback(async () => {
    if (!currentUser) {
      setAssetList([]);
      setCurrentAsset(null);
      return;
    }
    try {
      const assets = await fetchAllAssets();
      // Default order: descending by asset number (vandalId). numeric:true so
      // the numeric project-code portion compares as a number rather than
      // char-by-char (e.g. 40316… sorts above 9999…).
      const sortedAssets = assets.sort((a, b) =>
        (b.vandalId || "").localeCompare(a.vandalId || "", undefined, {
          numeric: true,
        })
      );
      console.log(sortedAssets.length);
      setAssetList(sortedAssets);
      // Update current asset if it exists in the new list
      if (currentAssetRef.current) {
        const updatedAsset = sortedAssets.find(
          (asset) => asset._id === currentAssetRef.current._id
        );
        if (updatedAsset && updatedAsset._id !== currentAssetRef.current._id) {
          setCurrentAsset(updatedAsset || null);
        }
      }
    } catch (error) {
      setAssetList([]);
      setCurrentAsset(null);
    }
  }, [currentUser?._id]);

  const getSettings = useCallback(async () => {
    if (!currentUser) {
      setSettings(null);
      return;
    }
    try {
      const settings = await fetchSettings();
      setSettings(settings);
    } catch (error) {
      setSettings(null);
    }
  }, [currentUser?._id]);

  const getAll = useCallback(async () => {
    console.log("refresh");
    await getProjects();
    await getAssets();
    await getSettings();
  }, [getProjects, getAssets, getSettings]);

  useEffect(() => {
    if (currentUser) {
      getAll();
    }
  }, [getAll, currentUser?._id]);

  const value = {
    projectList,
    setProjectList,
    currentProject,
    setCurrentProject,
    assetList,
    setAssetList,
    currentAsset,
    setCurrentAsset,
    settings,
    refreshProjects: getProjects,
    refreshAssets: getAssets,
    refreshAll: getAll,
  };

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};
